import { Typography } from "@mui/material";

import { TranslateFooterHTML, TranslationKey } from "components/Footer/i18n";

import { generateFooterAnalyticsProp } from "../../../../utils/generateAnalyticsTag";
import en from "../../i18n/en.json";
import { FooterHref } from "../../model";
import { ExpansionLinkGroupNodeProps } from "../ExpansionNode";

import { FooterLink, FooterLinkProps } from "./FooterLink";
import { linkGroupStyles as styles } from "./LinkGroup.styles";
import { LinkWithSuperTitleNode } from "./LinkWithSuperTitleNode";

interface CreateFooterLinkProps {
  clickOneTrustButton: VoidFunction;
  href: FooterHref;
  newTab?: boolean;
  pageLanguage: keyof FooterHref;
  setFeedbackFormOpen: (value: boolean) => void;
  titleKey: TranslationKey;
}

const createFooterLinkProps = ({
  clickOneTrustButton,
  href,
  newTab,
  pageLanguage,
  setFeedbackFormOpen,
  titleKey,
}: CreateFooterLinkProps): Omit<FooterLinkProps, "dataTestId"> => {
  if (titleKey === "feedback" || titleKey === "cookieSettings") {
    return {
      component: "button",
      css: [styles.linkText, styles.footerLinkButtons],
      onClick:
        titleKey === "feedback"
          ? () => setFeedbackFormOpen(true)
          : clickOneTrustButton,
    };
  }
  return {
    component: "a",
    css: styles.linkText,
    href: href[pageLanguage] || href.en,
    newTab: !!newTab,
  };
};

export const LinkGroup = ({
  clickOneTrustButton,
  isAuthenticated,
  node,
  pageLanguage,
  setFeedbackFormOpen,
  translate,
}: ExpansionLinkGroupNodeProps) => {
  return (
    <div css={styles.container}>
      <Typography variant="h5">{translate(node.titleKey)}</Typography>
      {node.childNodes.map((childNode) => {
        if (childNode.superTitleKey) {
          return (
            <LinkWithSuperTitleNode
              key={childNode.titleKey}
              href={childNode.href}
              newTab={childNode.newTab}
              pageLanguage={pageLanguage}
              superTitleKey={childNode.superTitleKey}
              titleKey={childNode.titleKey}
              translate={translate}
            />
          );
        }

        if (childNode.titleKey === "feedback" && !isAuthenticated) {
          return null;
        }

        const footerLinkProps = createFooterLinkProps({
          clickOneTrustButton,
          href: childNode.href,
          pageLanguage,
          setFeedbackFormOpen,
          titleKey: childNode.titleKey,
          newTab: childNode.newTab,
        });

        return (
          <FooterLink
            key={childNode.titleKey}
            dataTestId={childNode.titleKey}
            {...footerLinkProps}
            {...generateFooterAnalyticsProp({
              moduleName: en.translation[node.titleKey],
              name: en.translation[childNode.titleKey],
            })}
          >
            <Typography component="span" variant="body1">
              <TranslateFooterHTML keyName={childNode.titleKey} />
            </Typography>
          </FooterLink>
        );
      })}
    </div>
  );
};
